import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, FileText, CheckCircle, CreditCard, HelpCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import Layout from '../components/layout/Layout';
import { admissionSteps, requiredDocuments, images, applicationFormUrl } from '../data/mock';

const Admissions = () => {
  const faqs = [
    {
      question: 'What are the admission intakes?',
      answer: 'BIT offers two main intakes: March and September. The March Intake is available for Business, Tourism and Planning (BTP) and Science and Allied Technologies (SAT) departments. The September Intake is available for all departments including Health and Allied Sciences.',
    },
    {
      question: 'How long does the application process take?',
      answer: 'Applications are typically reviewed within 2-4 weeks. You will receive an email notification about your application status.',
    },
    {
      question: 'Can I apply for more than one programme?',
      answer: 'Yes, you can indicate a second choice programme on your application. However, priority will be given to your first choice.',
    },
    {
      question: 'Are there scholarships available?',
      answer: 'BIT offers various scholarship opportunities based on academic merit and financial need. Contact the admissions office for more information.',
    },
    {
      question: 'What if I do not meet the entry requirements?',
      answer: 'If you do not meet the standard entry requirements, you may be considered for a bridging programme. Contact admissions for guidance.',
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.business})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Admissions
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Your journey to a brighter future starts here. Learn about our admission process and requirements.
          </p>
        </div>
      </section>

      {/* Intakes Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Admission Intakes
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              BIT offers two main intakes per year to accommodate students with different schedules.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* March Intake */}
            <Card className="border-2 border-[#1e3a5f] shadow-lg">
              <CardHeader className="bg-[#1e3a5f] text-white">
                <div className="flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-[#d4a855]" />
                  <CardTitle className="text-2xl">March Intake</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-[#475569] mb-4">
                  The March Intake is available for selected departments.
                </p>
                <h4 className="font-semibold text-[#1e3a5f] mb-3">Available Departments:</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Business, Tourism and Planning (BTP)
                  </li>
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Science and Allied Technologies (SAT)
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* September Intake */}
            <Card className="border-2 border-[#d4a855] shadow-lg">
              <CardHeader className="bg-[#d4a855] text-[#142942]">
                <div className="flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-[#142942]" />
                  <CardTitle className="text-2xl">September Intake</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-[#475569] mb-4">
                  The September Intake is available for all departments, including Health programmes.
                </p>
                <h4 className="font-semibold text-[#1e3a5f] mb-3">Available Departments:</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Health and Allied Sciences
                  </li>
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Business, Tourism and Planning (BTP)
                  </li>
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Science and Allied Technologies (SAT)
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 bg-[#fef3c7] border border-[#d4a855] rounded-lg p-6 max-w-4xl mx-auto">
            <p className="text-[#92400e] text-center">
              <strong>Important:</strong> Health and Allied Sciences programmes (Pharmaceutical Sciences, Clinical Medicine, and Physiotherapy) are <strong>only available during the September intake</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              How to Apply
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Follow these simple steps to complete your application.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {admissionSteps.map((item) => (
              <Card key={item.step} className="border-none shadow-md card-hover relative">
                <CardContent className="pt-6">
                  <div className="absolute -top-4 left-6 w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-display font-semibold text-[#1e3a5f] text-lg mt-4 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#475569] text-sm">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6">
                Required Documents
              </h2>
              <p className="text-[#475569] mb-6">
                Ensure you have the following documents ready before starting your application:
              </p>
              <ul className="space-y-3">
                {requiredDocuments.map((doc, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-[#d4a855] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#1e3a5f] rounded-2xl p-8 text-white">
              <CreditCard className="w-12 h-12 text-[#d4a855] mb-4" />
              <h3 className="font-display text-2xl font-bold mb-4">Fees Information</h3>
              <p className="text-white/80 mb-6">
                Application processing fee and tuition fees vary by programme. Detailed fee structures are available upon request or during the application process.
              </p>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span className="text-white/70">Application Fee</span>
                  <span className="font-medium">Contact Admissions</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span className="text-white/70">Tuition Fees</span>
                  <span className="font-medium">Varies by Programme</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-white/70">Payment Plans</span>
                  <span className="font-medium">Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scholarships */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="container-custom">
          <Card className="border-none shadow-lg bg-white overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 md:p-12">
                <Badge className="bg-[#d4a855] text-[#142942] mb-4">Financial Support</Badge>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-4">
                  Scholarships & Financial Aid
                </h3>
                <p className="text-[#475569] mb-6">
                  BIT is committed to making quality education accessible. We offer various scholarship opportunities for deserving students based on academic merit and financial need.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Merit-based scholarships
                  </li>
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Need-based financial aid
                  </li>
                  <li className="flex items-center gap-2 text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                    Government loan assistance
                  </li>
                </ul>
                <Link to="/contact">
                  <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
                    Contact for Details
                  </Button>
                </Link>
              </div>
              <div
                className="hidden md:block bg-cover bg-center min-h-[300px]"
                style={{ backgroundImage: `url(${images.students})` }}
              />
            </div>
          </Card>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Find answers to common questions about admissions.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-6"
                >
                  <AccordionTrigger className="text-left font-medium text-[#1e3a5f] hover:text-[#2563eb] hover:no-underline py-4">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="w-5 h-5 text-[#d4a855] flex-shrink-0" />
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[#475569] pb-4 pl-8">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Apply?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Start your application today and take the first step towards your future career.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold px-8">
                Start Application
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8">
                Contact Admissions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Admissions;
